import { Box, Container, Text, useTheme, useToken } from '@chakra-ui/react';
import { hexToRgb } from '@onbeam/utils';
import { MotionBox } from '@sphere/ui';
import { useMotionValueEvent, useScroll, useSpring, useTransform } from 'framer-motion';
import { RefObject, useMemo, useRef, useState } from 'react';

interface Props extends PrismaJson.CollectionIntro {
  /** Optional background color, has to be a Chakra theme color setting */
  bgColor?: string;
  containerRef?: RefObject<HTMLElement>;
}

export const FocusedTextBlock = ({
  description,
  gradient,
  bgColor = 'black',
  containerRef,
}: Props) => {
  const theme = useTheme();
  const bgHex = useToken('colors', bgColor);
  const bgRgb = hexToRgb(bgHex);

  const items = useMemo(() => {
    /** We split the sentence at every dot, comma, exclamation mark or question mark */
    if (!description?.length) return [];

    const sentences = description.match(/[^\.,\?\!]+[\.\,\?\!]+/g);
    if (!sentences?.length) {
      if (description.length) return [description];
      return [];
    }

    /** Get the remainder of the initial string to handle cases where the user doesn't
     * end the string with any of the specified characters */
    const remainder = description?.substring(
      sentences.reduce((acc, curr) => acc + curr.length, 0),
      description.length,
    );

    /** Return the array of sentences with remainder (if it contains any characters) */
    return [...sentences, ...(remainder.length ? [remainder] : [])];
  }, [description]);

  /** Active sentence */
  const [active, setActive] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);

  /** Scroll progression handlers */
  const scrollProgressContainer = useRef(null);

  const { scrollYProgress } = useScroll({
    target: scrollProgressContainer,
    container: containerRef ? containerRef : undefined,
  });

  const scrollProgressWithSpring = useSpring(scrollYProgress, {
    stiffness: 300,
    damping: 40,
    restDelta: 0.001,
  });

  const opacityValue = useTransform(
    scrollProgressWithSpring,
    items.length
      ? items.flatMap((_, index) => [0.33 * index, 0.33 * (index + 0.5), 0.33 * (index + 1)])
      : [0, 0],
    items.length
      ? items.flatMap((_, index, self) => [
          !index ? 0 : 0.6,
          0,
          index === self.length - 1 ? 0 : 0.6,
        ])
      : [0, 0],
  );

  useMotionValueEvent(scrollProgressWithSpring, 'change', scrollPos => {
    setScrollPosition(scrollPos);
    setActive(Math.floor(((items.length + 2) * scrollPos * 100) / 100));
  });

  useMotionValueEvent(opacityValue, 'change', opacity => setOpacity(opacity));

  return (
    <Box bg={bgColor} h="800vh" ref={scrollProgressContainer}>
      <Container
        display="flex"
        alignItems="center"
        justifyContent="center"
        pos={scrollPosition >= 1 ? 'static' : 'sticky'}
        top={0}
        h="100vh"
      >
        <Box
          textAlign="center"
          bg={`linear-gradient(${theme.colors[bgColor]} 10%, ${gradient?.[0]} 20%, ${gradient?.[1]} 50% 80%, ${theme.colors[bgColor]} 90%)`}
          backgroundClip="text"
          lineHeight={1.2}
          bgSize="100% 800%"
          style={{ backgroundPosition: `100% ${scrollPosition * 100}%` }}
        >
          {items.map((item, index) => (
            <MotionBox
              key={item + index}
              as={Text}
              display="inline"
              fontSize={{ base: '2xl', md: '4xl', '2xl': '5xl' }}
              animate={{
                color: `rgba(${bgRgb}, ${active === index + 1 ? opacity : 0.8})`,
              }}
            >
              {item}
            </MotionBox>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
